import api from "@/interceptors";
import { IApiPromise } from "@/interfaces/responseData";
import { Assessment, Checklist } from "../apiRoutes/routes";
import { ICategories } from "@/interfaces/assessments/ICategories";
import {
  IAssessmentChecklist,
  IAssessmentChecklistItem,
  IAssessmentDetail,
  IChecklistInfoDetail,
  IChecklistMetrics,
  IChecklistScoreDetail,
} from "@/interfaces/assessments/IAssessmentDetails";
import { IContributor } from "@/interfaces/assessments/IContributors";
import {
  assessmentAdditionalInfoType,
  assessmentQuestionAnswerType,
  ASSESSMENT_ANSWER_STORE_TYPE,
  assessmentStatus,
  checklistStatusId,
} from "@/utils/constants";
import {
  convertUTCTimeToLocalTime,
  fileSizeFormat,
} from "@/utils/dateTimeUtils";
import utilities, { getComponentUiText } from "@/utils/utilities";
import { showErrorAPI } from "../apiRoutes";
import { IChecklistsReportDataResponse } from "@/interfaces/shares";
import cloneDeep from "lodash/cloneDeep";
import { IChecklistRequested } from "@/interfaces/vendor";
import store from "@/store";

// Push another question don't have header to it.
const OTHER_QUESTION = {
  itemMasterId: 0,
  checklistItemId: 0,
  checklistItemIsHeading: true,
  checklistItemHasChildren: true,
  checklistItemHeadingLabel: "Other",
  checklistItemDepth: 1,
  checklistChildren: [] as IAssessmentChecklistItem[],
};

export const TEXT_COLOR_WITH_GRADE_SCORE = {
  "A+": "#62BB47",
  A: "#62BB47",
  "A-": "#92C83F",
  "B+": "#92C83F",
  B: "#CDDC29",
  "B-": "#CDDC29",
  "C+": "#F7C816",
  C: "#F7C816",
  "C-": "#F68421",
  D: "#F68421",
  F: "#F14924",
};

export const isValidCompliance = (item: any, value: any) => {
  let isInvalid = false;
  value.forEach(({ percent, id }) => {
    if (id === 0) {
      const value =
        isInvalid ||
        item.itemCompleteValue > percent ||
        (!item.itemCompleteValue && item.itemCompleteValue !== 0);
      if (value) {
        isInvalid = true;
      }
    }
    if (id === 1) {
      const value =
        isInvalid ||
        item.itemCompleteValue < percent ||
        (!item.itemCompleteValue && item.itemCompleteValue !== 0);
      if (value) {
        isInvalid = true;
      }
    }
  });
  return !isInvalid;
};

export const isValidScoreIncrease = (item: any, value: any) => {
  let isInvalid = false;
  const scoreItem =
    Math.round((item.checklistItemScoreMax - item.checklistItemScore) * 100) /
    100;
  value.forEach(({ percent, id }) => {
    if (id === 0) {
      const value =
        isInvalid || scoreItem > percent || (!scoreItem && scoreItem !== 0);
      if (value) {
        isInvalid = true;
      }
    }
    if (id === 1) {
      const value =
        isInvalid || scoreItem < percent || (!scoreItem && scoreItem !== 0);
      if (value) {
        isInvalid = true;
      }
    }
  });
  return !isInvalid;
};

export const checkIsQuestionCompleted = (item: any) => {
  return (
    (item?.checklistItemComplianceTypeValue === null ||
      item?.checklistItemComplianceTypeValue === undefined) &&
    !item?.checklistItemCompensatingControlValue &&
    !item?.checklistItemComplianceOverride
  );
};
export const isPassedSearch = (
  item: any,
  searchKeyProps: string,
  fromSearchTable = false,
) => {
  const searchKey = searchKeyProps.toLowerCase();

  if (item.checklistItemName?.toLowerCase().includes(searchKey)) return true;

  if (fromSearchTable) {
    if (item.checklistItemNotes?.toLowerCase().includes(searchKey)) return true;
    if (item.checklistItemDescription?.toLowerCase().includes(searchKey))
      return true;
  }

  if (item.orderNumber?.toLowerCase().includes(searchKey)) return true;

  if (
    item.checklistItemHeadingLabel?.toLowerCase().includes(searchKey) ||
    item.checklistItemName?.toLowerCase().includes(searchKey)
  )
    return true;

  if (item.checklistItemCategoryLabel?.toLowerCase().includes(searchKey))
    return true;

  if (item.checklistItem2ndSubCategoryLabel) {
    for (const subCategory of item.checklistItem2ndSubCategoryLabel) {
      if (subCategory.toLowerCase().includes(searchKey)) return true;
    }
  }

  if (item.checklistItem3ndSubCategoryLabel) {
    for (const subCategory of item.checklistItem2ndSubCategoryLabel) {
      if (subCategory.toLowerCase().includes(searchKey)) return true;
    }
  }

  return false;
};

export const generateCheklistStatusIdToChecklistStatusName = (id: string) => {
  switch (id) {
    case checklistStatusId.created:
      return assessmentStatus.created;
    case checklistStatusId.inProgress:
      return assessmentStatus.inProgress;
    case checklistStatusId.waiting:
      return assessmentStatus.waiting;
    case checklistStatusId.paused:
      return assessmentStatus.paused;
    case checklistStatusId.other:
      return assessmentStatus.other;
    case checklistStatusId.completed:
      return assessmentStatus.completed;
    case checklistStatusId.defunct:
      return assessmentStatus.defunct;
    default:
      return assessmentStatus.waitingOnUs;
  }
};

export const generateGradeScoreWithCompletedScore = (score: number) => {
  switch (true) {
    case score > 90:
      return "A+";
    case score > 83.33:
      return "A";
    case score > 70:
      return "A-";
    case score > 66.67:
      return "B+";
    case score > 60:
      return "B";
    case score > 50:
      return "B-";
    case score > 40:
      return "C+";
    case score > 33.33:
      return "C";
    case score > 20:
      return "C-";
    case score > 16.667:
      return "D";
    case score > 0:
      return "F";

    default:
      return "F";
  }
};

export const generateGradeColorScoreWithCompletedScore = (val: number) => {
  switch (true) {
    case val > 90:
      return "tw-text-[#62BB47]";
    case val > 83.33:
      return "tw-text-[#62BB47]";
    case val > 70:
      return "tw-text-[#92C83F]";
    case val > 66.67:
      return "tw-text-[#92C83F]";
    case val > 60:
      return "tw-text-[#CDDC29]";
    case val > 50:
      return "tw-text-[#CDDC29]";
    case val > 40:
      return "tw-text-[#F7C816]";
    case val > 33.33:
      return "tw-text-[#F7C816]";
    case val > 20:
      return "tw-text-[#F68421]";
    case val > 16.667:
      return "tw-text-[#F68421]";
    case val > 0:
      return "tw-text-[#F14924]";

    default:
      return "tw-text-[#F14924]";
  }
};

export const getChecklistFromObjectTree = (
  checklistItemId: number,
  checklist: IAssessmentChecklistItem[],
): IAssessmentChecklistItem => {
  const getChecklistItem = (
    checklistItemId: number,
    listItem: IAssessmentChecklistItem[],
  ) => {
    if (listItem && Array.isArray(listItem)) {
      for (const item of listItem) {
        if (item.checklistItemId === checklistItemId) return item;
        if (
          item.checklistChildren &&
          Array.isArray(item.checklistChildren) &&
          item.checklistChildren.length
        ) {
          const foundInChildren = getChecklistItem(
            checklistItemId,
            item.checklistChildren,
          );
          if (foundInChildren) return foundInChildren;
        }
      }
    }

    return {} as IAssessmentChecklistItem;
  };

  const result = getChecklistItem(checklistItemId, checklist);
  return result;
};

export const convertComplianceInputValue = (answer: any) => {
  let temp = answer;
  temp = Math.round(parseFloat(answer));
  if (Number.isNaN(temp)) return null;
  else {
    temp = Math.max(0, Math.min(100, temp));
    return temp / 100;
  }
};

export const filterChecklistByName = (
  listQuestion: any[],
  valueSearch: string,
  searchNotes = false,
) => {
  const searchLowerCase = valueSearch.toLowerCase();
  if (!searchLowerCase.length) return listQuestion;

  listQuestion = listQuestion
    .map((item) => ({
      ...item,
      checklistChildren: item.checklistChildren?.filter((sub) => {
        if (
          sub.checklistItemName?.toLowerCase().includes(searchLowerCase) ||
          (searchNotes &&
            sub.checklistItemNotes?.toLowerCase().includes(searchLowerCase)) ||
          (searchNotes &&
            sub.checklistItemDescription
              ?.toLowerCase()
              .includes(searchLowerCase))
        )
          return true;

        if (sub.orderNumber?.toLowerCase().includes(searchLowerCase))
          return true;
      }),
    }))
    .filter(
      (item) =>
        item.checklistItemHeadingLabel
          ?.toLowerCase()
          .includes(searchLowerCase) ||
        item.checklistChildren?.some((sub) => {
          if (
            sub.checklistItemName?.toLowerCase().includes(searchLowerCase) ||
            (searchNotes &&
              sub.checklistItemNotes
                ?.toLowerCase()
                .includes(searchLowerCase)) ||
            (searchNotes &&
              sub.checklistItemDescription
                ?.toLowerCase()
                .includes(searchLowerCase))
          )
            return true;

          if (sub.orderNumber?.toLowerCase().includes(searchLowerCase))
            return true;
        }),
    );

  return [...listQuestion];
};

export const filterChecklistByComplete = (listQuestion: any[]): any[] => {
  return listQuestion
    .map((item) => {
      if (item.checklistItemHasChildren) {
        return {
          ...item,
          checklistChildren: filterChecklistByComplete(item.checklistChildren),
        };
      }
      return checkIsQuestionCompleted(item) ? item : null;
    })
    .filter(Boolean);
};

const pushAndSortChecklist = (checklist: any[], item: any) => {
  checklist.push(item);
  checklist.sort((a, b) => a.checklistItemOrder - b.checklistItemOrder);
  return checklist;
};

const isChecklistParent = (checklist: IAssessmentChecklistItem) => {
  const title =
    checklist.checklistItemHeadingLabel || checklist.checklistItemName || "";

  return (
    checklist.checklistItemDepth === 1 &&
    checklist.checklistItemHasChildren &&
    title.length > 0
  );
};

const isChecklistChild = (checklist: IAssessmentChecklistItem) => {
  if (!checklist.checklistItemPathId) return false;
  if (checklist.checklistItemPathId.length <= 1) return false;
  else return true;
};

const isChecklistQualifiedAddToParent = (
  checklist: IAssessmentChecklistItem,
  checklistItems: IAssessmentChecklistItem[],
) => {
  // Is Question.
  if (!checklist.checklistItemIsHeading && !checklist.checklistItemHasChildren)
    return true;

  // Check whether nested question have children or not.
  const items: IAssessmentChecklistItem[] = JSON.parse(
    JSON.stringify(checklistItems),
  );

  const child = items.filter(
    (i) => i.checklistItemPathId?.includes(checklist.checklistItemId),
  );

  if (child && child.length > 1) return true;
  else return false;
};

const isChecklistWithOtherIsHeading = (checklist: IAssessmentChecklistItem) => {
  return (
    !checklist.checklistItemIsHeading && !checklist.checklistItemHasChildren
  );
};

const checklistProcessDataBeforeSort = (
  item: any,
  categories: ICategories[],
) => {
  const category = categories.find(
    (cate) => cate.checklistItemCategoryId === item.checklistItemCategoryId,
  );
  const itemCompleteValue =
    item.checklistItemCompensatingControlValue ||
    item.checklistItemComplianceOverride ||
    item.checklistItemComplianceTypeValue;

  item.visibleState =
    item.checklistItemIsExcluded || item.isMaster ? "No" : "Yes";
  item.checklistItemCategoryLabel = category?.checklistItemCategoryLabel || "";
  item.itemCompleteValue =
    typeof itemCompleteValue === "number"
      ? Math.round(itemCompleteValue * 100)
      : undefined;
  item.checklistId = item.checklistItemId;

  item.answerType =
    item.checklistItemComplianceTypeId === "tf"
      ? assessmentQuestionAnswerType.radio
      : assessmentQuestionAnswerType.select;

  if (item.checklistItemAttachments && item.checklistItemAttachments.length)
    item = checklistProcessFileDataBeforeSort(item);

  return item;
};

export const checklistProcessFileDataBeforeSort = (item) => {
  item.additionalData = [
    {
      type: assessmentAdditionalInfoType.attachment,
      attachments: item.checklistItemAttachments,
    },
  ];
  if (item.checklistItemAttachments && item.checklistItemAttachments.length) {
    item.checklistItemAttachments.map((item) => {
      item["id"] = item.attachmentId || item.id;
      item["type"] = assessmentAdditionalInfoType.attachment;
      item["date"] = item.dateAdded || item.date;
      item["name"] = item.fileNameOriginal || item.name;
      item["size"] = item.fileSizeBytes
        ? fileSizeFormat(item.fileSizeBytes)
        : fileSizeFormat(item.size);
      item["uploadedBy"] = item.uploadedBy || item.addedByUserId;
      return item;
    });
  }
  return item;
};
export const getChecklistAssessmentScore = async () => {
  // TODO: TEMPORARY
  const randomTotalScore = Math.floor(Math.random() * (100 - 80 + 1)) + 80;
  const randomScore = Math.floor(Math.random() * 80);

  const scorePcnt = parseFloat(
    ((randomScore / randomTotalScore) * 100).toFixed(2),
  );

  return {
    checklistScore: randomScore,
    checklistScoreMax: randomTotalScore,
    checklistScorePcnt: scorePcnt,
  } as IChecklistScoreDetail;
};

const checklistProcessDataAfterSort = (list: any[], parentIndex = "") => {
  list.forEach((item, index) => {
    /**
     * Process data for header.
     * - Add Order number for header
     */

    item.orderNumber = parentIndex
      ? `${parentIndex}.${(index + 1).toString()}`
      : (index + 1).toString();
    item.checklistChildren?.forEach(
      (child) => (child.checklistId = item.checklistItemId),
    );

    /**
     * Process data for question.
     * - Add order number.
     */
    if (item.checklistChildren && item.checklistChildren.length) {
      item.checklistChildren = checklistProcessDataAfterSort(
        item.checklistChildren,
        item.orderNumber,
      );
    }
  });
  return list;
};

export const initialChecklistByCategory = (
  checklist: IAssessmentChecklistItem[],
  categories: ICategories[],
  orgSetup?: any,
) => {
  const otherQuestion = JSON.parse(JSON.stringify(OTHER_QUESTION));
  otherQuestion.checklistItemHeadingLabel = getComponentUiText(
    "app-component-assessments-input-question-other-1",
  );

  checklist.forEach((i) => {
    i = checklistProcessDataBeforeSort(i, categories);

    if (orgSetup) {
      const subName = getsubNameDuplicatePerType(i, orgSetup);
      i.subName = subName;
    }

    const isChild = isChecklistChild(i);

    // Handle add child to parent.
    if (isChild) {
      const parentId = i.checklistItemPathId[i.checklistItemPathId.length - 2];
      const parent = checklist.find((x) => x.checklistItemId === parentId);

      if (parent && isChecklistQualifiedAddToParent(i, checklist)) {
        /**
         * Check is Child and find a Parent
         * Save to Parent.
         */
        if (!parent.checklistChildren) parent.checklistChildren = [];
        parent.checklistChildren = pushAndSortChecklist(
          parent.checklistChildren,
          i,
        );
      } else if (isChecklistWithOtherIsHeading(i)) {
        /**
         * Check is Child and not find a Parent
         * Save to OTHER header.
         */
        otherQuestion.checklistChildren = pushAndSortChecklist(
          otherQuestion.checklistChildren,
          i,
        );
      }
    } else {
      /**
       * Check not Child and not qualified to Parent
       * Save to OTHER header.
       */
      const condition = !isChecklistParent(i);
      if (condition) {
        otherQuestion.checklistChildren = pushAndSortChecklist(
          otherQuestion.checklistChildren,
          i,
        );
      }
    }
  });

  /**
   * Only keep header (checklist depth = 1).
   * Sort by order.
   */
  checklist = checklist
    .filter((i) => isChecklistParent(i) && i.checklistChildren?.length)
    .sort((a, b) => a.checklistItemOrder - b.checklistItemOrder);

  if (otherQuestion.checklistChildren?.length)
    checklist.push(otherQuestion as IAssessmentChecklistItem);

  checklist = checklistProcessDataAfterSort(checklist);

  return checklist;
};

export const initialChecklistByCategoryForAssessmentQuestion = (
  checklist: IAssessmentChecklistItem[],
  categories: ICategories[],
) => {
  const otherQuestion = JSON.parse(JSON.stringify(OTHER_QUESTION));
  otherQuestion.checklistItemHeadingLabel = getComponentUiText(
    "app-component-assessments-input-question-other-1",
  );

  checklist.forEach((i) => {
    i = checklistProcessDataBeforeSort(i, categories);

    const isChild = isChecklistChild(i);

    // Handle add child to parent.
    if (isChild) {
      const parentId = i.checklistItemPathId[i.checklistItemPathId.length - 2];
      const parent = checklist.find((x) => x.checklistItemId === parentId);

      if (parent && isChecklistQualifiedAddToParent(i, checklist)) {
        /**
         * Check is Child and find a Parent
         * Save to Parent.
         */
        if (!parent.checklistChildren) parent.checklistChildren = [];
        parent.checklistChildren = pushAndSortChecklist(
          parent.checklistChildren,
          i,
        );
      } else if (isChecklistWithOtherIsHeading(i)) {
        /**
         * Check is Child and not find a Parent
         * Save to OTHER header.
         */
        otherQuestion.checklistChildren = pushAndSortChecklist(
          otherQuestion.checklistChildren,
          i,
        );
      }
    } else {
      /**
       * Check not Child and not qualified to Parent
       * Save to OTHER header.
       */
      const condition = !isChecklistParent(i);
      if (condition) {
        otherQuestion.checklistChildren = pushAndSortChecklist(
          otherQuestion.checklistChildren,
          i,
        );
      }
    }
  });

  /**
   * Only keep header (checklist depth = 1).
   * Sort by order.
   */
  checklist = checklist
    .filter((i) => isChecklistParent(i) && i.checklistChildren?.length)
    .sort((a, b) => a.checklistItemOrder - b.checklistItemOrder);

  const output = [] as IAssessmentChecklistItem[];
  let level: number;

  const orderChecklist = (
    array: IAssessmentChecklistItem[],
    parent: IAssessmentChecklistItem,
  ) => {
    array?.forEach((item, index) => {
      item.checklistId = parent.checklistItemId;
      if (!item.orderNumber) {
        item.orderNumber = parent.orderNumber
          ? `${parent.orderNumber}.${index + 1}`
          : `${index + 1}`;
      }
      orderChecklist(
        item.checklistChildren as IAssessmentChecklistItem[],
        item,
      );
    });
  };

  categories.forEach(({ checklistItemCategoryId }) => {
    level = 0;
    checklist.forEach((item) => {
      if (item.checklistItemCategoryId === checklistItemCategoryId) {
        level += 1;
        item.orderNumber = `${level}`;
        orderChecklist([item], item);
        output.push(item);
      }
    });
    const itemOther = {
      ...OTHER_QUESTION,
      checklistChildren: [],
    } as any;
    otherQuestion.checklistChildren?.forEach((item) => {
      if (item.checklistItemCategoryId === checklistItemCategoryId) {
        itemOther.checklistChildren.push(item);
      }
    });
    if (itemOther.checklistChildren.length) {
      level += 1;
      itemOther.orderNumber = `${level}`;
      orderChecklist([itemOther], itemOther);
      output.push(itemOther);
    }
  });
  return output;
};

export const getsubNameDuplicatePerType = (item, orgSetup) => {
  if (
    item.checklistItemPerTypeDuplicateTypeMetaId &&
    item.checklistItemPerTypeDuplicateTypeId &&
    item.checklistItemPerTypeDuplicateOrgToTypeId
  ) {
    let orgTypes, orgtypeObject, subName, isSite;
    switch (item.checklistItemPerTypeDuplicateTypeMetaId) {
      case "asset_type":
        if (item.checklistItemPerTypeDuplicateTypeId === "ip-domain") {
          orgTypes = orgSetup?.domains[0].values;
          orgtypeObject = orgTypes.find(
            (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
          );
          subName = orgtypeObject?.domainName;
          return subName;
        }
        if (item.checklistItemPerTypeDuplicateTypeId === "ip-brand") {
          orgTypes = orgSetup?.brands[0].values;
          orgtypeObject = orgTypes.find(
            (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
          );
          subName = orgtypeObject?.brandName;
          return subName;
        }
        break;

      case "service_sub_type":
        orgTypes = orgSetup?.webservices[0].values;
        if (item.checklistItemPerTypeDuplicateTypeId === "web") {
          orgtypeObject = orgTypes.find(
            (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
          );
          subName = orgtypeObject?.description;
          return subName;
        }
        if (item.checklistItemPerTypeDuplicateTypeId === "web-nat") {
          orgtypeObject = orgTypes.find(
            (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
          );
          subName = orgtypeObject?.appName;
          return subName;
        }
        break;

      case "service_type":
        if (item.checklistItemPerTypeDuplicateTypeId === "site") {
          orgTypes = orgSetup?.sites[0].values;
          orgtypeObject = orgTypes.find(
            (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
          );
          subName = orgtypeObject?.name;
          return subName;
        } else {
          const services = orgSetup?.services[0].values;
          orgtypeObject = services.find(
            (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
          );
          subName = orgtypeObject?.name;
          return subName;
        }
      case "country":
        orgTypes = orgSetup?.sites[0].values;
        orgtypeObject = orgTypes.find(
          (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
        );
        subName = orgtypeObject?.countryId;
        return subName;
      case "site_attribute_type":
        isSite = orgSetup.services[0]?.types[3]?.typeValues.find(
          (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
        )
          ? false
          : true;
        if (isSite) {
          orgTypes = orgSetup.sites[0].values;
          orgtypeObject = orgTypes.find(
            (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
          );
          subName = orgtypeObject?.name;
          return subName;
        } else {
          orgTypes = orgSetup.services[0].values;
          orgtypeObject = orgTypes.find(
            (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
          );
          subName = orgtypeObject?.name;
          return subName;
        }

      case "service_model_type":
        orgTypes = orgSetup.services[0].values;
        orgtypeObject = orgTypes.find(
          (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
        );
        subName = orgtypeObject?.name;
        return subName;

      case "site_type":
        orgTypes = orgSetup.sites[0].values;
        orgtypeObject = orgTypes.find(
          (i) => i.id === item.checklistItemPerTypeDuplicateOrgToTypeId,
        );
        subName = orgtypeObject?.name;
        return subName;
      default:
        return null;
    }
  }
  return null;
};

export const initialChecklistMaster = (masterAddable: any[]) => {
  const questionMaster = masterAddable.map((item) => ({
    ...item,
    checklistItemName: item.itemMasterName,
    checklistItemPathOrder: item.checklistToItemPathOrderSuggested,
    checklistItemOrder: item.checklistToItemOrderSuggested,
    isMaster: true,
    checklistItemId: item.itemMasterId,
    checklistItemComplianceTypeId: item.itemMasterComplianceTypeId,
    checklistItemComplianceTypeLabel: item.itemMasterComplianceType,
    checklistItemComplianceTypeName: item.itemMasterComplianceTypeName,
  }));

  return questionMaster;
};

export const initialChecklistOrigin = (
  checklist: IAssessmentChecklistItem[],
  checklistSorted: IAssessmentChecklistItem[],
  orgSetup?: any,
) => {
  for (let item of checklist) {
    if (orgSetup) {
      const subName = getsubNameDuplicatePerType(item, orgSetup);
      item.subName = subName;
    }
    const itemUpdated = getChecklistFromObjectTree(
      item.checklistItemId,
      checklistSorted,
    );
    item = JSON.parse(JSON.stringify(itemUpdated));
  }

  return checklist;
};

export const updateChecklistAnswer = async (
  assessmentQuestionTypes: any[],
  currentChecklistId: number,
  checklist: any,
  answer,
  type = ASSESSMENT_ANSWER_STORE_TYPE.COMPLIANCE_TYPE_VALUE,
) => {
  try {
    if (checklist.isMaster) {
      checklist.checklistItemComplianceTypeValue = null;
      checklist.checklistItemComplianceOverride = null;
      checklist.checklistItemCompensatingControlValue = null;
      store.dispatch("Assessment/updateChecklstAllItem", cloneDeep(checklist));
    } else {
      if (type === ASSESSMENT_ANSWER_STORE_TYPE.EXCLUDE_ITEM) {
        checklist.checklistItemIsExcluded = !checklist.checklistItemIsExcluded;
        checklist.visibleState = checklist.checklistItemIsExcluded
          ? "No"
          : "Yes";
      } else {
        if (type === ASSESSMENT_ANSWER_STORE_TYPE.COMPLIANCE_TYPE_VALUE)
          checklist.checklistItemComplianceTypeValue = answer;
        else if (type === ASSESSMENT_ANSWER_STORE_TYPE.COMPLIANCE_OVERRIDE)
          checklist.checklistItemComplianceOverride = answer;
        else if (type === ASSESSMENT_ANSWER_STORE_TYPE.COMPENSATING_CONTROL)
          checklist.checklistItemCompensatingControlValue = answer;

        if (
          typeof checklist.checklistItemComplianceTypeValue === "number" ||
          (checklist.checklistItemComplianceTypeValue &&
            !isNaN(checklist.checklistItemComplianceTypeValue))
        )
          checklist.itemCompleteValue =
            checklist.checklistItemComplianceTypeValue * 100;

        if (
          typeof checklist.checklistItemComplianceOverride === "number" ||
          (checklist.checklistItemComplianceOverride &&
            !isNaN(checklist.checklistItemComplianceOverride))
        )
          checklist.itemCompleteValue =
            checklist.checklistItemComplianceOverride * 100;

        if (
          typeof checklist.checklistItemCompensatingControlValue === "number" ||
          (checklist.checklistItemCompensatingControlValue &&
            !isNaN(checklist.checklistItemCompensatingControlValue))
        )
          checklist.itemCompleteValue =
            checklist.checklistItemCompensatingControlValue * 100;

        if (
          !checklist.checklistItemComplianceTypeValue &&
          checklist.checklistItemComplianceTypeValue?.toString() !== "0" &&
          !checklist.checklistItemComplianceOverride &&
          !checklist.checklistItemCompensatingControlValue
        ) {
          checklist.itemCompleteValue = undefined;
        }
      }

      checklist[type] = answer;
      const dataUpdate = { checklistItemId: checklist.checklistItemId };
      let dataChange = answer;
      if (type === ASSESSMENT_ANSWER_STORE_TYPE.COMPLIANCE_TYPE_VALUE) {
        const questionType = assessmentQuestionTypes.find(
          (type) => type.id == checklist.checklistItemComplianceTypeId,
        );
        if (answer === "") {
          dataChange = null;
        } else {
          const questionTypeValue = questionType.complianceTypeValues.find(
            (type) => type.value == answer,
          );
          dataChange = questionTypeValue.id;
        }
      } else if (type === ASSESSMENT_ANSWER_STORE_TYPE.NOTES) {
        checklist.checklistItemNotes = answer;
      }

      store.dispatch("Assessment/updateChecklstAllItem", cloneDeep(checklist));
      store.commit(
        "Assessment/UPDATE_CHECKLIST_ALL_ITEM",
        cloneDeep(checklist),
      );

      dataUpdate[type] = dataChange;
      const params = {
        checklistId: currentChecklistId,
        checklistItems: [dataUpdate],
      };
      const res: any = await postAnswerQuestion(params);
      if (res?.data?.data?.checklistItems?.length) {
        checklist.checklistItemScore =
          res.data.data.checklistItems[0].checklistItemScore;
        checklist.checklistItemScoreMax =
          res.data.data.checklistItems[0].checklistItemScoreMax;
      }

      return true;
    }

    return checklist;
  } catch (error) {
    showErrorAPI(error, Assessment.postAnswerQuestion);
  }
};

export const getCheckListFromMasterAll = (
  payload: object,
): IApiPromise<string[]> =>
  api.post(Assessment.checkListFromMasterAll, payload);

export const getCheckListIdMetricsCompletion = (
  checklistId: string,
): IApiPromise<any> =>
  api.get(
    Assessment.checkListIdMetricsCompletion.replace(
      ":checklistId",
      checklistId,
    ),
  );

export const getCheckListIdMetricsCountCompliance = (
  checklistId: string,
): IApiPromise<any> =>
  api.get(
    Assessment.checkListIdMetricsCountCompliance.replace(
      ":checklistId",
      checklistId,
    ),
    { params: { complianceLessThan: 1 } } as any,
  );

export const getDetailFromCheckListId = (
  checkListId: number,
): IApiPromise<IChecklistInfoDetail> =>
  api.get(
    Assessment.getDetailFromCheckListId.replace(
      ":checkListId",
      checkListId.toString(),
    ),
  );

export const getMetricFromChecklistId = (
  checkListId: number,
): IApiPromise<IChecklistMetrics> =>
  api.get(
    Checklist.getChecklistToItemMetric.replace(
      ":checklistId",
      checkListId.toString(),
    ),
  );

export const getInputComplianceTypes = (
  checklistContextIds = "",
  includeArchived: any,
): IApiPromise<any> => {
  let routes = Assessment.getInputComplianceTypes as string;

  if (checklistContextIds !== "")
    routes = routes + `?checklistContextIds[]=${checklistContextIds}`;

  return api.get(routes, {
    params: {
      includeArchived,
    },
  });
};

export const getAssessmentCategories = (
  checkListId: number,
): IApiPromise<ICategories[]> =>
  api.get(
    Assessment.getCategories.replace(":checkListId", checkListId.toString()),
  );
export const handleGetAttachment = (attachmentId: number): IApiPromise<[]> =>
  api.get(
    Assessment.getAttachment.replace(":attachmentId", attachmentId.toString()),
  );

export const getAssessmentChecklists = (
  categoryId: string,
  checklistId: number,
  showMasterItem = false,
  includeExcluded = true,
): IApiPromise<IAssessmentChecklist> =>
  api.get(
    Assessment.getChecklistsByCategory.replace(
      /:checklistId|:categoryId/gi,
      (match) => {
        if (match === ":checklistId") return checklistId.toString();
        if (match === ":categoryId") return categoryId;
        return "";
      },
    ),
    {
      params: {
        includeMasterItems: showMasterItem,
        includeExcluded: includeExcluded,
      },
    },
  );

export const getChecklistContributors = (
  checklistId: number,
): IApiPromise<{ contributors: IContributor[] }> =>
  api.get(
    Assessment.getChecklistContributors.replace(
      ":checklistId",
      checklistId.toString(),
    ),
  );

export const postAnswerQuestion = (
  params: any,
): IApiPromise<{ contributors: IContributor[] }> =>
  api.post(Assessment.postAnswerQuestion, params);

export const postDuplicateQuestion = (
  params: any,
): IApiPromise<{ contributors: IContributor[] }> =>
  api.post(Assessment.postDuplicateQuestion, params);

export const postToggleChecklistItems = async (
  checklistId: number,
  checklistItemIds: number[],
): IApiPromise<any> =>
  await api.post(Checklist.toggleChecklistToItem, {
    checklistId,
    checklistItemIds,
  });

export const getChecklistAll = (
  checklistId: number,
): IApiPromise<IAssessmentChecklist> =>
  api.get(
    Assessment.getChecklistAll.replace(":checklistId", checklistId.toString()),
  );
export const getUserUploadFile = (userId: string): IApiPromise<any> =>
  api.get(Assessment.getUserUploadFile.replace(":userId", userId));

export const getAllChecklist = (): IApiPromise<IAssessmentDetail[]> =>
  api.get(Assessment.getChecklists);

export const getChecklistReport = (
  checklistId: number,
): IApiPromise<IChecklistsReportDataResponse> =>
  api.get(
    Checklist.getChecklistReportData.replace(
      ":checklistId",
      checklistId.toString(),
    ),
  );

export const getChecklistCSV = (checklistId: number): IApiPromise<any> =>
  api.get(
    Checklist.getChecklistCSV.replace(":checklistId", checklistId.toString()),
  );

export const getTemplate = (): IApiPromise<any[]> =>
  api.get(Assessment.getTemplate, {
    params: { includeHomeOrg: true },
  });

export const getTemplateDetail = (teamplateId): IApiPromise<any[]> =>
  api.get(
    Assessment.getTemplateDetail.replace(":templateId", teamplateId.toString()),
    {
      params: { includeHomeOrg: true },
    },
  );

export const getCheckListDuplicate = (
  params: any,
  payload?: { checklistName?: string; checklistDescription?: string },
): IApiPromise<string[]> =>
  api.post(
    Assessment.getCheckListDuplicate.replace(":checklistId", params),
    payload || {},
    { params: { allowAnyStatus: true } } as any,
  );

export const createChecklistToItem = (params: any): IApiPromise<any> =>
  api.post(Checklist.createChecklistToItem, params);

export const deleteChecklistTemplate = (params: any): IApiPromise<any> =>
  api.delete(Checklist.deleteChecklistTemplate, { data: params });

export const addItemFromMaster = (
  checklistId: any,
  masterItemIds: any,
  categoryId: any,
): IApiPromise<any> =>
  api.post(Checklist.addItemFromMaster.replace(":checklistId", checklistId), {
    masterItemIds,
    categoryId,
  });

export const getCheckListStatusDetails = (
  checkListId: number,
): IApiPromise<string[]> =>
  api.get(
    Assessment.checklistStatus.replace(":checklistId", checkListId.toString()),
  );

export const putChecklistStatus = (
  checklistId: number,
  data: any,
): IApiPromise<any> =>
  api.put(
    Assessment.checklistStatus.replace(":checklistId", checklistId.toString()),
    { ...data },
  );
export const putChecklistRename = (
  checklistId: number,
  data: any,
): IApiPromise<any> =>
  api.put(
    Assessment.checklistRename.replace(":checklistId", checklistId.toString()),
    { ...data },
  );

export const getChecklist = (checklistId: number): IApiPromise<any> =>
  api.get(
    Assessment.checklistRename.replace(":checklistId", checklistId.toString()),
  );

export const makeParentNonHierarchical = (parent, parents) => {
  if (!parent) return parents;
  parents.push(parent);
  return makeParentNonHierarchical(parent.checklistParent, parents);
};
export const processChecklistAssessmentData = async (
  checklists: IAssessmentDetail[],
) => {
  const result: IAssessmentDetail[] = [];
  if (!checklists || checklists.length === 0) return result;

  // get checklist tree by checklistParent, push child to checklist.history
  const checklistTree: any = [];
  checklists.forEach((e, index) => {
    if (e.checklistParent) {
      const parentsData = makeParentNonHierarchical(e.checklistParent, []);
      const parents = parentsData.filter(
        (item: any) =>
          item.checklistIsArchived === false ||
          item.checklistStatusId !== checklistStatusId.defunct,
      );
      parents.push({
        ...e,
        name: e.checklistName,
        date: convertUTCTimeToLocalTime(e.checklistDateCreated),
        state: e.checklistStatus,
        isActive: false,
        id: index + 2,
      });
      parents.sort(
        (a, b) =>
          new Date(b.checklistDateCreated).getTime() -
          new Date(a.checklistDateCreated).getTime(),
      );
      e.checklistItem = cloneDeep(parents);
      e.checklistAssessmentHistory = cloneDeep(parents);
    } else {
      e.checklistItem = cloneDeep([e]);
      e.checklistAssessmentHistory = cloneDeep([e]);
    }
    checklistTree.push({
      ...e,
      name: e.checklistName,
      date: convertUTCTimeToLocalTime(e.checklistDateCreated),
      state: e.checklistStatus,
      isActive: false,
      id: index + 2,
    });
  });
  const checklistTreeWithoutArchived: any = [];
  const tempChecklistTreeWithoutArchived = checklistTree.filter(
    (item: any) =>
      item.checklistIsArchived === false ||
      item.checklistStatusId !== checklistStatusId.defunct,
  );
  checklistTree.forEach((item: any) => {
    if (
      item.checklistIsArchived === false ||
      item.checklistStatusId !== checklistStatusId.defunct
    ) {
      checklistTreeWithoutArchived.push(item);
    } else {
      let isFoundParent = false;
      if (item.checklistItem && item.checklistItem.length > 0) {
        item.checklistItem.splice(0, 1);
        // find parent of item which is not parent of any other chain
        let oldChain = item.checklistItem;
        item.checklistItem.forEach((child: any, index) => {
          if (!isFoundParent && tempChecklistTreeWithoutArchived.length > 0) {
            const parent = tempChecklistTreeWithoutArchived.find(
              (parentItem: any) =>
                parentItem.checklistId === child.checklistId ||
                (parentItem.checklistItem &&
                  parentItem.checklistItem.find(
                    (e: any) => e.checklistId === child.checklistId,
                  )),
            );
            if (!parent) {
              isFoundParent = true;
              checklistTreeWithoutArchived.push({
                ...child,
                checklistItem: cloneDeep(oldChain),
                checklistAssessmentHistory: cloneDeep(oldChain),
                name: child.checklistName,
                date: convertUTCTimeToLocalTime(child.checklistDateCreated),
                state: child.checklistStatus,
                isActive: false,
                id: index + 2,
              });
            } else {
              oldChain = oldChain.filter(
                (e: any) => e.checklistId !== child.checklistId,
              );
            }
          }
        });
      }
    }
  });
  return checklistTreeWithoutArchived.sort(
    (a, b) =>
      new Date(b.checklistDateCreated).getTime() -
      new Date(a.checklistDateCreated).getTime(),
  );
};

const getTime = (time: string) => {
  return convertUTCTimeToLocalTime(time);
};

export const processChecklistAssessmentDataNewVerson = (data) => {
  const checklistTree: any = [];
  data.forEach((e, index) => {
    if (e.checklistParent) {
      const parentsData = makeParentNonHierarchical(e.checklistParent, []);
      const parents = parentsData.filter(
        (item: any) =>
          item.checklistIsArchived === false ||
          item.checklistStatusId !== checklistStatusId.defunct,
      );
      parents.push(e);
      parents.sort(
        (a, b) =>
          new Date(b.checklistDateCreated).getTime() -
          new Date(a.checklistDateCreated).getTime(),
      );
      delete parents[0].checklistItem;
      delete parents[0].checklistAssessmentHistory;
      e.checklistItem = cloneDeep(parents);
      e.checklistAssessmentHistory = cloneDeep(parents);
    } else {
      e.checklistItem = cloneDeep([e]);
      e.checklistAssessmentHistory = cloneDeep([e]);
    }
    checklistTree.push({
      ...e,
      name: e.checklistName,
      date: getTime(e.checklistDateCreated),
      state: e.checklistStatus,
      isActive: false,
      id: index + 2,
    });
  });
  const checklistTreeWithoutArchived: any = [];
  const tempChecklistTreeWithoutArchived = checklistTree.filter(
    (item: any) =>
      item.checklistIsArchived === false ||
      item.checklistStatusId !== checklistStatusId.defunct,
  );
  checklistTree.forEach((item: any) => {
    if (
      item.checklistIsArchived === false ||
      item.checklistStatusId !== checklistStatusId.defunct
    ) {
      checklistTreeWithoutArchived.push(item);
    } else {
      let isFoundParent = false;
      if (item.checklistItem && item.checklistItem.length > 0) {
        item.checklistItem.splice(0, 1);
        // find parent of item which is not parent of any other chain
        let oldChain = item.checklistItem;
        item.checklistItem.forEach((child: any, index) => {
          if (!isFoundParent && tempChecklistTreeWithoutArchived.length > 0) {
            const parent = tempChecklistTreeWithoutArchived.find(
              (parentItem: any) =>
                parentItem.checklistId === child.checklistId ||
                (parentItem.checklistItem &&
                  parentItem.checklistItem.find(
                    (e: any) => e.checklistId === child.checklistId,
                  )),
            );
            if (!parent) {
              isFoundParent = true;
              checklistTreeWithoutArchived.push({
                ...child,
                checklistItem: cloneDeep(oldChain),
                checklistAssessmentHistory: cloneDeep(oldChain),
                name: child.checklistName,
                date: getTime(child.checklistDateCreated),
                state: child.checklistStatus,
                isActive: false,
                id: index + 2,
              });
            } else {
              oldChain = oldChain.filter(
                (e: any) => e.checklistId !== child.checklistId,
              );
            }
          }
        });
      }
    }
  });
  return checklistTreeWithoutArchived.sort(
    (a, b) =>
      new Date(b.checklistDateCreated).getTime() -
      new Date(a.checklistDateCreated).getTime(),
  );
};

export const postUploadFile = (
  checklistItemId: number,
  formData: FormData,
): IApiPromise<any> =>
  api.post(
    Assessment.checklistAttachment.replace(
      ":checklistItemId",
      checklistItemId.toString(),
    ),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

export const deleteFiles = async (attachmentIds: number[]) =>
  await api.delete(Assessment.checklistAttachments, {
    data: { attachmentIds },
  });

export const getAttachmentData = (attachmentId: number) =>
  api.get(
    Checklist.getChecklistAttachment.replace(
      ":attachmentId",
      attachmentId.toString(),
    ),
  );

export const getImportanceAssessment = (): IApiPromise<any> =>
  api.get(Assessment.getImportanceAssessment);

export const getImpactAssessment = (): IApiPromise<any> =>
  api.get(Assessment.getImpactAssessment);

export const getEffortAssessment = (): IApiPromise<any> =>
  api.get(Assessment.getEffortAssessment);

export const getCostAssessment = (): IApiPromise<any> =>
  api.get(Assessment.getCostAssessment);

export const postChecklistTemplate = (params: {
  checklistId: number;
  data: {
    templateName: string;
    templateVersion?: string;
    templateOrder?: number;
    templateDescription?: string;
    templateNotes?: string;
    orgId?: number;
  };
}): IApiPromise<any> => {
  const { checklistId, data } = params ?? {};
  const path = Assessment.postChecklistTemplate.replace(
    ":checklistId",
    checklistId.toString(),
  );
  return api.post(path, data);
};

export const putChecklistTemplate = (params: {
  checklistId: number;
  templateId: number;
}): IApiPromise<any> => {
  const { checklistId, templateId } = params ?? {};
  const path = Assessment.putChecklistTemplate
    .replace(":checklistId", checklistId.toString())
    .replace(":templateId", templateId.toString());
  return api.put(path);
};

export const deleteChecklist = (checklistId: number): IApiPromise<any> => {
  return api.delete(
    Assessment.deleteChecklist.replace(":checklistId", checklistId.toString()),
  );
};

export const getChecklistRequested = (
  checklistRequestedId: number,
): IApiPromise<IChecklistRequested> =>
  api.get(
    Checklist.getChecklistRequested.replace(
      ":checklistRequestedId",
      checklistRequestedId.toString(),
    ),
  );

export const getAssessmentItemsCSV = (
  checklistRequestedId: number,
): IApiPromise<File> => {
  return api.get(
    Checklist.getAssessmentItemsCSV.replace(
      ":checklistRequestedId",
      checklistRequestedId.toString(),
    ),
    {
      responseType: "blob",
    },
  );
};

const getDropdownData = (questionType: any, prospectQuestionTypes: any) => {
  const data = prospectQuestionTypes.find((item) => item.id === questionType);
  return data?.complianceTypeValues || [];
};

export const assessmentQuestionSubCateListCheckIsSelectedAll = (
  data: IAssessmentChecklistItem,
  propsSelectedQuestions: number[],
) => {
  const allQuestionOrderNumbers: number[] | undefined =
    data.checklistChildren?.map(
      (item: IAssessmentChecklistItem) => item.checklistItemId,
    );

  const isAllSelected = allQuestionOrderNumbers?.every((item) =>
    propsSelectedQuestions.includes(item),
  );
  return !!isAllSelected;
};

export const assessmentQuestionSubCateListformatData = (
  data,
  propsChecklistId,
  prospectQuestionTypes,
) => {
  try {
    const array = data.map((item) => {
      const temp: any = {
        ...item,
        answerType:
          item?.checklistItemComplianceTypeId === "tf"
            ? assessmentQuestionAnswerType.radio
            : assessmentQuestionAnswerType.select,
        dropdownData:
          item?.checklistItemComplianceTypeId === "tf"
            ? []
            : getDropdownData(
                item?.checklistItemComplianceTypeId,
                prospectQuestionTypes,
              ),
        additionalData: [],
        checklistChildren: item?.checklistChildren?.length
          ? assessmentQuestionSubCateListformatData(
              item?.checklistChildren,
              propsChecklistId,
              prospectQuestionTypes,
            )
          : [],
      };
      if (
        temp.checklistItemAttachments &&
        temp.checklistItemAttachments.length > 0
      ) {
        temp.additionalData.push({
          type: assessmentAdditionalInfoType.attachment,
          attachments: item.checklistItemAttachments.map((item) => ({
            ...item,
            name: item.fileNameOriginal,
            size: utilities.formatBytes(item.fileSizeBytes),
            dateAdded: item.dateAdded,
            addedByUserId: item.addedByUserId,
            checklistId: propsChecklistId,
          })),
        });
      }
      return temp;
    });
    return array;
  } catch (error) {
    return [];
  }
};

export default {
  updateChecklistAnswer,
  pushAndSortChecklist,
  convertComplianceInputValue,
  filterChecklistByComplete,
  filterChecklistByName,
  isChecklistParent,
  checklistProcessDataBeforeSort,
  deleteFiles,
  checklistProcessDataAfterSort,
  initialChecklistByCategory,
  initialChecklistMaster,
  getChecklistCSV,
  deleteChecklist,
  isPassedSearch,
  processChecklistAssessmentData,
  getsubNameDuplicatePerType,
  assessmentQuestionSubCateListCheckIsSelectedAll,
  assessmentQuestionSubCateListformatData,
};
